/* iter205 — Office Billing Review queue.
   Lists office_review invoice drafts submitted by techs via the completion wizard's
   "Office to Finalize & Submit Invoice" outcome. The office finalizes / edits /
   returns / voids each one. Finalizing converts the draft IN PLACE into a real
   invoice (no disconnected duplicate), preserving the job + technician link. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Search, CheckCircle2, ClipboardCheck, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDT = (v) => { if (!v) return ''; try { return new Date(v).toLocaleString(); } catch { return String(v); } };
const ageDays = (v) => { if (!v) return ''; try { const d = Math.floor((Date.now() - new Date(v).getTime()) / 86400000); return d <= 0 ? 'today' : `${d}d`; } catch { return ''; } };

const BASIS_LABEL = {
  final_known: 'Final amount known',
  previously_quoted: 'Previously quoted',
  office_to_determine: 'Office to determine',
};

// status: 'office_review_required' | 'returned_to_tech' | 'finalized' | 'voided'
const OfficeBillingReview = ({ status = 'office_review_required', onCountsChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(null); // row in the detail drawer

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/office-billing-review?status=${status}`, { withCredentials: true });
      setItems(data?.items || []);
      if (onCountsChange && data?.counts) onCountsChange(data.counts);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load office billing review');
      setItems([]);
    }
    setLoading(false);
  }, [status, onCountsChange]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return items;
    return items.filter((it) => [it.customer_name, it.payor_name, it.work_order_number, it.technician_name, it.work_performed, it.description]
      .filter(Boolean).join(' ').toLowerCase().includes(n));
  }, [items, q]);

  const afterAction = () => { setActive(null); load(); };

  return (
    <div data-testid={`office-review-${status}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#525252]" />
          <Input data-testid="office-review-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Customer, work order, tech, work performed…" className="pl-8" />
        </div>
        <Button size="sm" onClick={load} disabled={loading} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-center text-[#A3A3A3] py-16">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-[#262626] rounded-lg bg-[#0d0d0d]" data-testid="office-review-empty">
          <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-3" />
          <div className="text-white font-semibold">Nothing here</div>
          <div className="text-xs text-[#A3A3A3] mt-1">No submissions in this queue.</div>
        </div>
      ) : (
        <div className="border border-[#262626] rounded-lg overflow-x-auto" data-testid="office-review-table">
          <table className="w-full text-sm">
            <thead className="bg-[#141414] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
              <tr>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">WO#</th>
                <th className="text-left px-3 py-2">Service Date</th>
                <th className="text-left px-3 py-2">Tech</th>
                <th className="text-right px-3 py-2">Proposed</th>
                <th className="text-right px-3 py-2">Quoted</th>
                <th className="text-left px-3 py-2">Basis / Reason</th>
                <th className="text-left px-3 py-2">Addl</th>
                <th className="text-left px-3 py-2">Submitted</th>
                <th className="text-left px-3 py-2">Age</th>
                <th className="text-right px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.invoice_id} className="border-t border-[#262626] hover:bg-[#141414]" data-testid={`office-review-row-${r.invoice_id}`}>
                  <td className="px-3 py-2 text-white whitespace-nowrap">{r.customer_name || '—'}
                    {r.payor_name && r.payor_name !== r.customer_name && <div className="text-[10px] text-[#A3A3A3]">Payor: {r.payor_name}</div>}
                  </td>
                  <td className="px-3 py-2 text-[#D4D4D4]">{r.work_order_number || '—'}</td>
                  <td className="px-3 py-2 text-[#D4D4D4] whitespace-nowrap">{r.service_date || '—'}</td>
                  <td className="px-3 py-2 text-[#D4D4D4] whitespace-nowrap">{r.technician_name || '—'}</td>
                  <td className="px-3 py-2 text-right text-white whitespace-nowrap">{r.amount_pending ? <span className="text-amber-300">Pending</span> : fmtMoney(r.amount)}</td>
                  <td className="px-3 py-2 text-right text-[#A3A3A3] whitespace-nowrap">{r.estimate_amount != null ? fmtMoney(r.estimate_amount) : '—'}</td>
                  <td className="px-3 py-2 text-[#D4D4D4] max-w-[220px]">
                    <div className="text-[11px]">{BASIS_LABEL[r.amount_basis] || r.amount_basis}</div>
                    {r.reason_not_collected && <div className="text-[10px] text-[#A3A3A3] truncate" title={r.reason_not_collected}>{r.reason_not_collected}</div>}
                    {r.return_reason && <div className="text-[10px] text-orange-300 truncate" title={r.return_reason}>Returned: {r.return_reason}</div>}
                  </td>
                  <td className="px-3 py-2">{r.additional_work ? <Badge className="text-[10px] bg-orange-500/10 border-orange-500/40 text-orange-200">Yes</Badge> : <span className="text-[#525252]">—</span>}</td>
                  <td className="px-3 py-2 text-[11px] text-[#A3A3A3] whitespace-nowrap">{fmtDT(r.submitted_at)}</td>
                  <td className="px-3 py-2 text-[11px] text-[#D4D4D4] whitespace-nowrap">{ageDays(r.submitted_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" data-testid={`office-review-open-${r.invoice_id}`} onClick={() => setActive(r)}
                      className="text-xs border-[#3a3a3a]">Review</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ReviewDrawer row={active} onClose={() => setActive(null)} onDone={afterAction} />
    </div>
  );
};

const ReviewDrawer = ({ row, onClose, onDone }) => {
  const [amount, setAmount] = useState('');
  const [markQb, setMarkQb] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const editable = row && row.office_review_status === 'office_review_required';

  useEffect(() => {
    if (row) { setAmount(row.amount_pending ? '' : String(row.amount ?? '')); setMarkQb(false); setReason(''); }
  }, [row]);

  if (!row) return null;

  const post = async (path, body) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/office-billing-review/${row.invoice_id}/${path}`, body, { withCredentials: true });
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
      return false;
    } finally { setBusy(false); }
  };

  const finalize = async () => {
    if (amount === '' || Number(amount) <= 0) { toast.error('Set the invoice amount before finalizing.'); return; }
    if (await post('finalize', { amount: Number(amount), mark_entered_in_qb: markQb })) {
      toast.success('Invoice finalized'); onDone();
    }
  };
  const saveAmount = async () => {
    if (amount === '' || Number(amount) < 0) { toast.error('Enter a valid amount.'); return; }
    setBusy(true);
    try {
      await axios.put(`${API}/admin/office-billing-review/${row.invoice_id}`, { amount: Number(amount) }, { withCredentials: true });
      toast.success('Amount saved'); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  const returnToTech = async () => {
    if (!reason.trim()) { toast.error('A reason is required to return to the tech.'); return; }
    if (await post('return-to-tech', { reason: reason.trim() })) { toast.success('Returned to technician'); onDone(); }
  };
  const voidDraft = async () => {
    if (!reason.trim()) { toast.error('A reason is required to void.'); return; }
    if (await post('void', { reason: reason.trim() })) { toast.success('Draft voided'); onDone(); }
  };

  return (
    <Sheet open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-xl overflow-y-auto" data-testid="office-review-drawer">
        <SheetHeader>
          <SheetTitle className="text-white flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-amber-300" /> Office Review — {row.customer_name}</SheetTitle>
          <SheetDescription className="text-[#A3A3A3]">Finalize, edit, return, or void this office-review invoice draft. Finalizing converts it into a real invoice in place — the job & technician stay linked.</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <Info l="Work Order" v={row.work_order_number || '—'} />
            <Info l="Service Date" v={row.service_date || '—'} />
            <Info l="Technician" v={row.technician_name || '—'} />
            <Info l="Basis" v={BASIS_LABEL[row.amount_basis] || row.amount_basis} />
            <Info l="Payor" v={row.payor_name || row.customer_name} />
            <Info l="Quoted Amount" v={row.estimate_amount != null ? fmtMoney(row.estimate_amount) : '—'} />
          </div>
          {row.helpers?.length > 0 && (
            <p className="text-xs text-[#A3A3A3]">Helpers: {row.helpers.map((h) => h.name).filter(Boolean).join(', ')}</p>
          )}
          <div className="rounded-md border border-[#262626] bg-[#0A0A0A] p-3 space-y-1">
            <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">Work Performed</p>
            <p className="text-sm text-white whitespace-pre-wrap">{row.work_performed || row.description || '—'}</p>
            {row.materials && <p className="text-xs text-[#A3A3A3]">Materials: {row.materials}</p>}
            {row.equipment_notes && <p className="text-xs text-[#A3A3A3]">Equipment: {row.equipment_notes}</p>}
            {row.labor_notes && <p className="text-xs text-[#A3A3A3]">Labor: {row.labor_notes}</p>}
            {row.payment_terms && <p className="text-xs text-[#A3A3A3]">Terms: {row.payment_terms}</p>}
            {row.po_number && <p className="text-xs text-[#A3A3A3]">PO#: {row.po_number}</p>}
            {row.additional_work && <p className="text-xs text-orange-300">Additional work: {row.additional_work_notes || 'yes'}</p>}
            {row.customer_approval_name && <p className="text-xs text-[#A3A3A3]">Customer approval: {row.customer_approval_name}</p>}
            {row.billing_notes && <p className="text-xs text-[#A3A3A3]">Notes: {row.billing_notes}</p>}
          </div>

          {editable ? (
            <>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">Invoice Amount</label>
                <div className="flex items-center gap-2 mt-1">
                  <Input type="number" inputMode="decimal" data-testid="office-review-amount" value={amount}
                    onChange={(e) => setAmount(e.target.value)} placeholder={row.amount_pending ? 'Set the amount' : ''}
                    className="bg-[#0A0A0A] border-[#262626] text-white w-40" />
                  <Button size="sm" variant="outline" onClick={saveAmount} disabled={busy} data-testid="office-review-save-amount" className="border-[#3a3a3a] text-xs">Save Amount</Button>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={markQb} onChange={(e) => setMarkQb(e.target.checked)} data-testid="office-review-mark-qb" />
                Mark entered in QuickBooks on finalize
              </label>
              <Button onClick={finalize} disabled={busy} data-testid="office-review-finalize" className="w-full bg-green-600 hover:bg-green-500 text-white">
                Finalize Invoice
              </Button>

              <div className="pt-3 border-t border-[#262626] space-y-2">
                <label className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">Reason (for return / void)</label>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="office-review-reason"
                  className="bg-[#0A0A0A] border-[#262626] text-white" rows={2} placeholder="Required to return to tech or void" />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={returnToTech} disabled={busy} data-testid="office-review-return" className="flex-1 border-orange-500/40 text-orange-300">Return to Tech</Button>
                  <Button variant="outline" onClick={voidDraft} disabled={busy} data-testid="office-review-void" className="flex-1 border-red-500/40 text-red-300">Void Draft</Button>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-md border border-[#262626] bg-[#0A0A0A] p-3">
              <p className="text-sm text-white">Status: <span className="font-medium">{(row.office_review_status || '').replace(/_/g, ' ')}</span></p>
              {row.office_review_status === 'finalized' && <p className="text-xs text-green-300 mt-1">Finalized — ${Number(row.amount).toFixed(2)}. This is now a real invoice.</p>}
              {row.return_reason && <p className="text-xs text-orange-300 mt-1">Return reason: {row.return_reason}</p>}
              {row.void_reason && <p className="text-xs text-red-300 mt-1">Void reason: {row.void_reason}</p>}
            </div>
          )}
        </div>

        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-[#A3A3A3] hover:text-white" aria-label="Close"><X className="w-4 h-4" /></button>
      </SheetContent>
    </Sheet>
  );
};

const Info = ({ l, v }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">{l}</p>
    <p className="text-sm text-white">{v}</p>
  </div>
);

export default OfficeBillingReview;
